import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  HomeIcon,
  BriefcaseIcon,
  BuildingStorefrontIcon,
  DocumentArrowDownIcon,
  DocumentArrowUpIcon,
  CogIcon,
  Bars3Icon,
  XMarkIcon,
  SunIcon,
  MoonIcon,
  MapPinIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline'
import { useState } from 'react'
import { useTheme } from '../contexts/ThemeContext.tsx'

const navigation = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  { name: 'Jobs', href: '/jobs', icon: BriefcaseIcon },
  { name: 'Stores', href: '/stores', icon: BuildingStorefrontIcon },
  { name: 'Cities', href: '/cities', icon: MapPinIcon },
  { name: 'Import', href: '/import', icon: DocumentArrowUpIcon },
  { name: 'Export', href: '/export', icon: DocumentArrowDownIcon },
  { name: 'Cost Management', href: '/costs', icon: CurrencyDollarIcon },
  { name: 'Settings', href: '/settings', icon: CogIcon },
]

interface LayoutProps {
  children: React.ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(false)
  const location = useLocation()
  const { theme, toggleTheme } = useTheme()
  const logoUrl = 'https://cdn2.vapetasia.com/img/vaporscraper-logo-small.png'
  const currentYear = new Date().getFullYear()
  const poweredBy = `Powered By: Vapetasia © ${currentYear}`
  return (
    <div className='h-screen flex overflow-hidden bg-gray-100 dark:bg-gray-900 mb-10'>
      {/* Mobile sidebar */}
      <div
        className={`fixed inset-0 flex z-40 md:hidden ${
          sidebarOpen ? '' : 'hidden'
        }`}
      >
        <div
          className='fixed inset-0 bg-gray-600 bg-opacity-75'
          onClick={() => setSidebarOpen(false)}
        />
        <div className='relative flex-1 flex flex-col max-w-xs w-full bg-white dark:bg-gray-800'>
          <div className='absolute top-0 right-0 -mr-12 pt-2'>
            <button
              type='button'
              className='ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white'
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className='h-6 w-6 text-white' />
            </button>
          </div>
          <div className='flex-1 h-0 pt-5 pb-4 overflow-y-auto flex flex-col'>
            <div className='flex-shrink-0 flex flex-col items-center px-4 space-y-2'>
              <h1 className='text-xl font-bold text-gray-900 dark:text-white'>
                VaporScraper Web App
              </h1>

              <img src={logoUrl} alt='VaporScraper' className='h-16 w-auto' />
            </div>
            <nav className='mt-5 px-2 space-y-1 flex-1'>
              {navigation.map((item) => {
                const isActive = location.pathname === item.href
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`${
                      isActive
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                    } group flex items-center px-2 py-2 text-base font-medium rounded-md`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon
                      className={`${
                        isActive
                          ? 'text-gray-500 dark:text-gray-400'
                          : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400'
                      } mr-4 flex-shrink-0 h-6 w-6`}
                    />
                    {item.name}
                  </Link>
                )
              })}
            </nav>
            {/* Mobile Sidebar Footer */}
            <div className='px-4 py-3 border-t border-gray-200 dark:border-gray-700'>
              <p className='text-xs text-center text-gray-500 dark:text-gray-400'>
                1.0.1
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div
        className={`hidden md:flex md:flex-shrink-0 transition-all duration-300 ${
          desktopSidebarCollapsed ? '' : ''
        }`}
      >
        <div
          className={`flex flex-col transition-all duration-300 ${
            desktopSidebarCollapsed ? 'w-16' : 'w-64'
          }`}
        >
          <div className='flex flex-col h-0 flex-1 '>
            <div className='flex items-center h-16 flex-shrink-0 px-4 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 justify-between'>
              <div className='flex-1'>
                {!desktopSidebarCollapsed && (
                  <div className='flex flex-col items-center space-y-1 py-2'>
                    {/* <h1 className='text-xl font-bold text-gray-900 dark:text-white'>
                      VaporScraper
                    </h1> */}
                    <img
                      src={logoUrl}
                      alt='VaporScraper'
                      className='h-12 w-auto object-contain'
                    />
                  </div>
                )}
              </div>
              <button
                onClick={() =>
                  setDesktopSidebarCollapsed(!desktopSidebarCollapsed)
                }
                className='p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors'
                title={
                  desktopSidebarCollapsed
                    ? 'Expand sidebar'
                    : 'Collapse sidebar'
                }
              >
                <Bars3Icon className='h-6 w-6' />
              </button>
            </div>
            <div className='flex-1 flex flex-col overflow-y-auto'>
              <nav className='flex-1 px-2 py-4 bg-white dark:bg-gray-800 space-y-1 border-r border-gray-200 dark:border-gray-700'>
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`${
                        isActive
                          ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                      } group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        desktopSidebarCollapsed ? 'justify-center' : ''
                      }`}
                      title={desktopSidebarCollapsed ? item.name : ''}
                    >
                      <item.icon
                        className={`${
                          isActive
                            ? 'text-gray-500 dark:text-gray-400'
                            : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400'
                        } ${
                          desktopSidebarCollapsed ? '' : 'mr-3'
                        } flex-shrink-0 h-6 w-6`}
                      />
                      {!desktopSidebarCollapsed && item.name}
                    </Link>
                  )
                })}
              </nav>
              {/* Desktop Sidebar Footer */}
              {!desktopSidebarCollapsed && (
                <div className='px-4 py-3 bg-white dark:bg-gray-800 border-r border-t border-gray-200 dark:border-gray-700'>
                  <p className='text-xs text-center text-gray-500 dark:text-gray-400'>
                    1.0.1
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className='flex flex-col w-0 flex-1 overflow-hidden'>
        <div className='relative z-10 flex-shrink-0 flex h-16 bg-white dark:bg-gray-800 shadow dark:shadow-gray-900'>
          <button
            type='button'
            className='px-4 border-r border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 md:hidden'
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className='h-6 w-6' />
          </button>
          <div className='flex-1 px-4 flex justify-between items-center'>
            <div className='flex-1 flex'>
              <div className='w-full flex md:ml-0'>
                <div className='relative w-full text-gray-400 dark:text-gray-500 focus-within:text-gray-600 dark:focus-within:text-gray-400'>
                  <div className='absolute inset-y-0 left-0 flex flex-col justify-center pointer-events-none'>
                    <span className='text-lg font-bold text-gray-700 dark:text-gray-200'>
                      VaporScraper Web App
                    </span>
                    <span className='text-xs text-gray-500 dark:text-gray-400 ml-5'>
                      {poweredBy}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className='ml-4 flex items-center'>
              <button
                onClick={toggleTheme}
                className='p-2 rounded-lg text-gray-500 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors'
                title={
                  theme === 'dark'
                    ? 'Switch to light mode'
                    : 'Switch to dark mode'
                }
              >
                {theme === 'dark' ? (
                  <SunIcon className='h-6 w-6' />
                ) : (
                  <MoonIcon className='h-6 w-6' />
                )}
              </button>
            </div>
          </div>
        </div>

        <main className='flex-1 relative overflow-y-auto focus:outline-none'>
          <div className='py-6'>
            <div className='max-w-7xl mx-auto px-4 sm:px-6 md:px-8'>
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}
